import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import api from '../services/api';
import { registrationAPI } from '../services/api';
import { useAuth } from './AuthContext';

interface RegistrationStatus {
  id: string; // eventId or hackathonId
  type: 'event' | 'hackathon';
  isRegistered: boolean;
  status?: string; // 'registered', 'confirmed', etc.
  requiresApproval?: boolean;
  registration?: any;
  lastChecked: number;
  userId: string; // Add user ID to prevent cross-user cache pollution
}

interface RegistrationContextType {
  registrationStatuses: Map<string, RegistrationStatus>;
  checkRegistration: (id: string, type: 'event' | 'hackathon') => Promise<boolean>;
  getRegistrationStatus: (id: string) => RegistrationStatus | null;
  clearRegistrationStatus: (id: string) => void;
  clearAllRegistrations: () => void;
  isChecking: (id: string) => boolean;
  unregisterFromEvent: (eventId: string) => Promise<void>;
  unregisterFromHackathon: (hackathonId: string, leaveTeam?: boolean) => Promise<void>;
  registerForEvent: (eventId: string, data?: any) => Promise<void>;
  registerForHackathon: (hackathonId: string, data?: any) => Promise<void>;
}

const RegistrationContext = createContext<RegistrationContextType | undefined>(undefined);

export const useRegistration = () => {
  const context = useContext(RegistrationContext);
  if (context === undefined) {
    throw new Error('useRegistration must be used within a RegistrationProvider');
  }
  return context;
};

interface RegistrationProviderProps {
  children: React.ReactNode;
}

export const RegistrationProvider: React.FC<RegistrationProviderProps> = ({ children }) => {
  const [registrationStatuses, setRegistrationStatuses] = useState<Map<string, RegistrationStatus>>(new Map());
  const [checkingRegistrations, setCheckingRegistrations] = useState<Set<string>>(new Set());
  const { user, isLoading } = useAuth();
  
  // Use refs to access current state values without causing re-renders
  const registrationStatusesRef = useRef(registrationStatuses);
  const checkingRegistrationsRef = useRef(checkingRegistrations);
  
  // Update refs when state changes
  registrationStatusesRef.current = registrationStatuses;
  checkingRegistrationsRef.current = checkingRegistrations;

  // Clear registration cache when user changes
  useEffect(() => {
    if (user?._id) {
      console.log('🔄 User changed, clearing registration cache for user:', user.email);
      setRegistrationStatuses(new Map());
      setCheckingRegistrations(new Set());
    }
  }, [user?._id]); // Only depend on user ID, not the entire user object

  const checkRegistration = useCallback(async (id: string, type: 'event' | 'hackathon'): Promise<boolean> => {
    // Don't check if user is not loaded or not authenticated
    if (isLoading || !user) {
      console.log('🔍 Skipping registration check - user not loaded or not authenticated');
      return false;
    }

    // Create user-specific cache key
    const cacheKey = `${user._id}:${id}`;

    // Don't check if already checking this registration
    if (checkingRegistrationsRef.current.has(cacheKey)) {
      console.log('🔍 Already checking registration for:', id);
      return registrationStatusesRef.current.get(cacheKey)?.isRegistered || false;
    }

    // Check if we have a recent status (less than 30 seconds old)
    const existingStatus = registrationStatusesRef.current.get(cacheKey);
    const now = Date.now();
    if (existingStatus && (now - existingStatus.lastChecked) < 30000) {
      console.log('🔍 Using cached registration status for:', id);
      return existingStatus.isRegistered;
    }

    console.log('🔍 Checking registration for:', id, 'Type:', type, 'User:', user.email);
    
    try {
      setCheckingRegistrations(prev => new Set(prev).add(cacheKey));
      
      let response;
      if (type === 'hackathon') {
        response = await api.get(`/registrations/hackathon/${id}/check`);
      } else {
        response = await api.get(`/registrations/event/${id}/check`);
      }
      
      console.log('✅ Registration check response:', response.data);
      
      const newStatus: RegistrationStatus = {
        id,
        type,
        isRegistered: true,
        status: response.data.status,
        requiresApproval: type === 'event' ? response.data.event?.requiresAdminApproval : false,
        registration: response.data,
        lastChecked: now,
        userId: user._id
      };
      
      setRegistrationStatuses(prev => new Map(prev).set(cacheKey, newStatus));
      return true;
      
    } catch (err: any) {
      if (err.response?.status === 404) {
        console.log('❌ User not registered for:', id);
        
        const newStatus: RegistrationStatus = {
          id,
          type,
          isRegistered: false,
          lastChecked: now,
          userId: user._id
        };
        
        setRegistrationStatuses(prev => new Map(prev).set(cacheKey, newStatus));
        return false;
      } else {
        console.error('❌ Error checking registration:', err);
        // Don't update status on error, return existing status if available
        return existingStatus?.isRegistered || false;
      }
    } finally {
      setCheckingRegistrations(prev => {
        const newSet = new Set(prev);
        newSet.delete(cacheKey);
        return newSet;
      });
    }
  }, [user?._id, isLoading]); // Only depend on stable user ID and loading state

  const getRegistrationStatus = useCallback((id: string): RegistrationStatus | null => {
    if (!user?._id) return null;
    const cacheKey = `${user._id}:${id}`;
    return registrationStatuses.get(cacheKey) || null;
  }, [registrationStatuses, user?._id]);

  const clearRegistrationStatus = useCallback((id: string) => {
    if (!user?._id) return;
    const cacheKey = `${user._id}:${id}`;
    setRegistrationStatuses(prev => {
      const newMap = new Map(prev);
      newMap.delete(cacheKey);
      return newMap;
    });
  }, [user?._id]);

  const clearAllRegistrations = useCallback(() => {
    setRegistrationStatuses(new Map());
  }, []);

  const isChecking = useCallback((id: string): boolean => {
    if (!user?._id) return false;
    const cacheKey = `${user._id}:${id}`;
    return checkingRegistrations.has(cacheKey);
  }, [checkingRegistrations, user?._id]);

  const registerForEvent = useCallback(async (eventId: string, data?: any) => {
    try {
      console.log('🔄 Registering for event:', eventId);
      
      const response = await registrationAPI.registerForEvent(eventId, data);
      console.log('✅ Event registration response:', response);
      
      // Update registration status
      const newStatus: RegistrationStatus = {
        id: eventId,
        type: 'event',
        isRegistered: true,
        registration: response.registration,
        lastChecked: Date.now(),
        userId: user?._id || ''
      };
      
      if (user?._id) {
        const cacheKey = `${user._id}:${eventId}`;
        setRegistrationStatuses(prev => new Map(prev).set(cacheKey, newStatus));
      }
      
      console.log('✅ Successfully registered for event:', eventId);
    } catch (error) {
      console.error('❌ Error registering for event:', error);
      throw error;
    }
  }, [user?._id]);

  const registerForHackathon = useCallback(async (hackathonId: string, data?: any) => {
    try {
      console.log('🔄 Registering for hackathon:', hackathonId);
      
      const response = await registrationAPI.registerForHackathon(hackathonId, data);
      console.log('✅ Hackathon registration response:', response);
      
      // Update registration status
      const newStatus: RegistrationStatus = {
        id: hackathonId,
        type: 'hackathon',
        isRegistered: true,
        registration: response.registration,
        lastChecked: Date.now(),
        userId: user?._id || ''
      };
      
      if (user?._id) {
        const cacheKey = `${user._id}:${hackathonId}`;
        setRegistrationStatuses(prev => new Map(prev).set(cacheKey, newStatus));
      }
      
      console.log('✅ Successfully registered for hackathon:', hackathonId);
    } catch (error) {
      console.error('❌ Error registering for hackathon:', error);
      throw error;
    }
  }, [user?._id]);

  const unregisterFromEvent = useCallback(async (eventId: string) => {
    try {
      console.log('🔄 Unregistering from event:', eventId);
      
      const response = await api.delete(`/registrations/event/${eventId}/unregister`);
      console.log('✅ Unregister response:', response);
      
      // Clear the registration status for this event
      clearRegistrationStatus(eventId);
      
      console.log('✅ Successfully unregistered from event:', eventId);
    } catch (error) {
      console.error('❌ Error unregistering from event:', error);
      throw error;
    }
  }, [clearRegistrationStatus]);

  const unregisterFromHackathon = useCallback(async (hackathonId: string, leaveTeam: boolean = true) => {
    try {
      console.log('🔄 Unregistering from hackathon:', hackathonId, 'leaveTeam:', leaveTeam);
      
      const response = await registrationAPI.unregisterFromHackathon(hackathonId, leaveTeam);
      console.log('✅ Unregister response:', response);
      
      // Clear the registration status for this hackathon
      clearRegistrationStatus(hackathonId);
      
      console.log('✅ Successfully unregistered from hackathon:', hackathonId);
    } catch (error) {
      console.error('❌ Error unregistering from hackathon:', error);
      throw error;
    }
  }, [clearRegistrationStatus]);

  const value: RegistrationContextType = {
    registrationStatuses,
    checkRegistration,
    getRegistrationStatus,
    clearRegistrationStatus,
    clearAllRegistrations,
    isChecking,
    unregisterFromEvent,
    unregisterFromHackathon,
    registerForEvent,
    registerForHackathon
  };

  return (
    <RegistrationContext.Provider value={value}>
      {children}
    </RegistrationContext.Provider>
  );
}; 